//
import parsePhoneNumber, { PhoneNumber } from 'libphonenumber-js';
import StringUtils from '@common/utils/StringUtils';
import Languages from '@main/definitions/Languages';


interface NumberFomat
{
  [key:number] : Intl.NumberFormat;
}

//
//
//
export class Localize
{

    public language             : string = Languages.Type.AMERICAN;

    public languages            : Array<string> = [Languages.Type.AMERICAN];

    private language_data       : any;

    private currencyDigits  : NumberFomat = {};
    private numberDigits    : NumberFomat = {};

    public date_short!          : Intl.DateTimeFormat;
    public date_normal!         : Intl.DateTimeFormat;
    public date_long!           : Intl.DateTimeFormat;

    public date_time_short!     : Intl.DateTimeFormat;
    public date_time_normal!    : Intl.DateTimeFormat;
    public date_time_long!      : Intl.DateTimeFormat;

    public time_short!     : Intl.DateTimeFormat;
    public time_normal!    : Intl.DateTimeFormat;
    public time_long!      : Intl.DateTimeFormat;


    ///////////////////////////////////////////////////////////////////////////////////////////////////
    constructor()
    {
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////
    public currency( value : number | null, digits : number ) : string
    {
        if( value === null )return "";
        
        // create cache
        if( !this.currencyDigits[ digits ] )
        {
            this.currencyDigits[ digits ] = new Intl.NumberFormat( this.language, { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits });
        }
        return this.currencyDigits[ digits ].format( value );
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////
    public number( value : number | null | undefined, digits : number ) : string
    {
        if( value === undefined )
            return "0";
        else if( value !== null )
        {
            // create cache
            if( !this.numberDigits[ digits ] )
            {
                this.numberDigits[ digits ] = new Intl.NumberFormat( this.language, { style: 'decimal', minimumFractionDigits: digits, maximumFractionDigits: digits });
            }
            return this.numberDigits[ digits ].format( value );
        }
        else
            return "";
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////
    public date( value : Date | null, format : Localize.Format ) : string
    {
        if( value === null || isNaN( value.getTime() ) )return "";
        switch( format )
        {
            case  Localize.Format.SHORT : return this.date_short.format( value ); break;
            case  Localize.Format.MEDIUM : return this.date_normal.format( value ); break;
            case  Localize.Format.LONG : return this.date_long.format( value ); break;
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////
    public time( value : Date | null, format : Localize.Format ) : string
    {
        if( value === null || isNaN( value.getTime() ) )return "";
        switch( format )
        {
            case  Localize.Format.SHORT : return this.time_short.format( value ); break;
            case  Localize.Format.MEDIUM : return this.time_normal.format( value ); break;
            case  Localize.Format.LONG : return this.time_long.format( value ); break;
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////
    public dateTime( value : Date | null, format : Localize.Format ) : string
    {
        if( value === null || isNaN( value.getTime() ) )return "";
        switch( format )
        {
            case  Localize.Format.SHORT : return this.date_time_short.format( value ); break;
            case  Localize.Format.MEDIUM : return this.date_time_normal.format( value ); break;
            case  Localize.Format.LONG : return this.date_time_long.format( value ); break;
        }
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    public async setLanguage( lang : string, data : any ) : Promise<void>
    {
        this.language = lang;
        this.language_data = data;

        // re-create under new language
        const ckeys : Array<string> = Object.keys( this.currencyDigits );
        ckeys.forEach( ( key : string ) => { this.currencyDigits[ parseInt( key ) ] = new Intl.NumberFormat( this.language, { style: 'currency', currency: 'USD', minimumFractionDigits: parseInt( key ), maximumFractionDigits: parseInt( key ) });  } );

        const nkeys : Array<string> = Object.keys( this.numberDigits );
        nkeys.forEach( ( nkey : string ) => { this.numberDigits[ parseInt( nkey ) ] = new Intl.NumberFormat( this.language, { style: 'decimal', minimumFractionDigits: parseInt( nkey ), maximumFractionDigits: parseInt( nkey ) });  } );

        this.date_short   = new Intl.DateTimeFormat( lang, { dateStyle: 'short' });
        this.date_normal  = new Intl.DateTimeFormat( lang, { dateStyle: 'medium' });
        this.date_long    = new Intl.DateTimeFormat( lang, { dateStyle: 'long' });

        this.date_time_short   = new Intl.DateTimeFormat( lang, { dateStyle: 'short', timeStyle: "short" });
        this.date_time_normal  = new Intl.DateTimeFormat( lang, { dateStyle: 'medium', timeStyle: "medium" });
        this.date_time_long    = new Intl.DateTimeFormat( lang, { dateStyle: 'long', timeStyle: "long" });

        this.time_short   = new Intl.DateTimeFormat( lang, { timeStyle: "short" });
        this.time_normal  = new Intl.DateTimeFormat( lang, { timeStyle: "medium" });
        this.time_long    = new Intl.DateTimeFormat( lang, { timeStyle: "long" });

    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    public phone( phone : string ) : string
    {
        const phoneNumber : PhoneNumber | undefined = parsePhoneNumber( phone , 'US' );
        return phoneNumber ? phoneNumber.formatNational() : phone;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    public label( path : string, options? : Localize.LabelOptions ) : string
    {
        if( this.language_data[ path ] )
        {
            let str : string = this.language_data[ path ];
            if( options !== undefined )
            {
                if( options.bullet !== undefined && options.bullet === true )str = StringUtils.format( "{0} {1}", StringUtils.BULLET, str );
                if( options.lowerCase !== undefined && options.lowerCase === true )str = str.toLowerCase();
                if( options.colon !== undefined && options.colon === true )str += ": ";
                if( options.ellipse !== undefined && options.ellipse === true )str += StringUtils.ELLIPSE;
                if( options.bracket !== undefined && options.bracket === true )str = StringUtils.format( "({0})", str );
                if( options.quotes !== undefined && options.quotes === true )str = StringUtils.format( '"{0}"', str );
                if( options.required !== undefined && options.required === true )str = StringUtils.format( '{0}*', str );
            }
            
            return str;
        }
        else
            return StringUtils.format( "!{0}", path );
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    public amount( path : string, default_amt : number ) : number
    {
        if( this.language_data[ path ] )
            return parseInt( this.language_data[ path ] );
        else
            return default_amt;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    public minutes( min : number ) : string
    {
        if( min < 60 )
        {
            return `${min} minute${min === 1 ? '' : 's'}`;
        }
        // less than a day
        else if (min < 1440)
        { 
            const hours : number = min / 60;
            const rounded : number = parseFloat(hours.toFixed(1));
            return rounded === 1
                ? `${rounded} hour`
                : `${rounded} hours`;
        }
        // less than a month (30 days)
        else if ( min < 43200)
        { 
            const days : number = min / 1440;
            const rounded : number = parseFloat(days.toFixed(1));
            return rounded === 1
                ? `${rounded} day`
                : `${rounded} days`;
        }
        else
        {
            const months : number = min / 43200;
            const rounded : number = parseFloat(months.toFixed(1));
            return rounded === 1
                ? `${rounded} month`
                : `${rounded} months`;
        }
    }


}

export namespace Localize
{
    export interface LabelOptions
    {
        lowerCase? : boolean;
        colon? : boolean;
        ellipse? : boolean;
        bracket? : boolean;
        bullet? : boolean;
        quotes? : boolean;
        required? : boolean;
    }

    export enum Format
    {
        SHORT = "short",
        MEDIUM = "medium",
        LONG = "long"
    }
}

export default Localize;
